/**
 * QR Tarayıcı Modal — kamera ile QR okur.
 * html5-qrcode kütüphanesi kullanılır.
 */

import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Camera, AlertCircle, X } from 'lucide-react'
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'

interface Props {
  acik: boolean
  onKapat: () => void
  /** QR okunduğunda metin içeriği ile çağrılır */
  onOkundu: (icerik: string) => void
  /** Kamera üstündeki yönerge metni (varsayılan: şube QR) */
  aciklama?: string
}

const SCANNER_ID = 'qr-scanner-element'

export function QrScannerModal({ acik, onKapat, onOkundu, aciklama }: Props) {
  const [hata, setHata] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const okunduRef = useRef(false) // tek seferlik tetikleyici

  useEffect(() => {
    if (!acik) {
      // Modal kapanırsa scanner'ı durdur
      durdurVeTemizle()
      return
    }

    okunduRef.current = false
    setHata(null)
    setYukleniyor(true)

    // DOM hazır olsun diye küçük gecikme
    const t = setTimeout(() => {
      basla()
    }, 100)

    return () => {
      clearTimeout(t)
      durdurVeTemizle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik])

  async function basla() {
    try {
      const el = document.getElementById(SCANNER_ID)
      if (!el) {
        setHata('Tarayıcı elemanı bulunamadı.')
        setYukleniyor(false)
        return
      }

      const html5QrCode = new Html5Qrcode(SCANNER_ID, { verbose: false })
      scannerRef.current = html5QrCode

      await html5QrCode.start(
        { facingMode: 'environment' }, // arka kamera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          if (okunduRef.current) return
          okunduRef.current = true
          onOkundu(decodedText)
        },
        () => {
          // Per-frame error — yoksay
        }
      )

      setYukleniyor(false)
    } catch (err) {
      console.error('QR scanner hata:', err)
      const e = err as Error
      if (e.message?.includes('Permission') || e.name === 'NotAllowedError') {
        setHata('Kamera izni gerekli. Tarayıcı ayarlarından izin ver.')
      } else if (e.name === 'NotFoundError' || e.message?.includes('camera')) {
        setHata('Kamera bulunamadı.')
      } else {
        setHata('Kamera başlatılamadı: ' + e.message)
      }
      setYukleniyor(false)
    }
  }

  async function durdurVeTemizle() {
    const sc = scannerRef.current
    if (sc) {
      try {
        if (sc.getState() === Html5QrcodeScannerState.SCANNING) {
          await sc.stop()
        }
        await sc.clear()
      } catch {
        // sessizce yok say
      }
      scannerRef.current = null
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="QR Oku" boyut="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 text-center">
          {aciklama ?? 'Şubedeki Shift Beat QR kodunu kameraya tut.'}
        </p>

        <div className="relative bg-black rounded-2xl overflow-hidden aspect-square">
          <div id={SCANNER_ID} className="w-full h-full" />

          {yukleniyor && !hata && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center text-white">
                <Camera className="w-16 h-16 mx-auto mb-3 animate-pulse" />
                <p className="text-sm">Kamera başlatılıyor...</p>
              </div>
            </div>
          )}

          {hata && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90 p-4">
              <div className="text-center text-white">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-sm">{hata}</p>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onKapat}
          className="btn-secondary w-full flex items-center justify-center gap-2"
        >
          <X className="w-4 h-4" />
          <span>İptal</span>
        </button>
      </div>
    </Modal>
  )
}
